<template>
    <b-container fluid class="top-padding bottom-padding no-horiz-padding">

        <h2>Your Site Settings</h2>

        <div v-if='(!isAuthenticated)'>
            Please sign in to view your site settings.
            <login-control prompt="Sign In Now" :show-as-link="false"/>
        </div>

        <div v-if='(isAuthenticated)'>
            <p>
                Below are your settings for Handweaving.net, including tieup orientation for draft display, thumbnails view size for draft listings, and filters for draft searching.  You can view and change these here and also where they are used on the site pages.
            </p>

            <hr/>
            <h2>
                Tieup Orientation
            </h2>
            <div> Show the tieup on the upper-left, upper-right, lower-right, or lower-left.</div>
            <br/>
            <div class="inline-auto-width right-padding align-top">
                Tieup<br />
                <table>
                    <tr>
                        <td class="medium-right-padding"><input type='radio' v-model="orientation" name='orientation' value="tl" /></td>
                        <td class="medium-right-padding"><input type='radio' v-model="orientation" name='orientation' value="tr" /></td>
                    </tr>
                    <tr>
                        <td class="medium-right-padding"><input type='radio' v-model="orientation" name='orientation' value="bl" /></td>
                        <td class="medium-right-padding"><input type='radio' v-model="orientation" name='orientation' value="br" /></td>
                    </tr>
                </table>
            </div>

            <div class="inline-auto-width right-padding align-top">
                <router-link :to='buildDraftDetailUrl(this.pattern)' :title="this.pattern.Tooltip">
                    <img class="draft-image-thumbnail" :src="draftImageUrlFunction(this.pattern.PatternID, this.thumbnailSize+1, this.orientation, false, false, false, false, false, false, false, false, false, false, this.pattern.ImagesExist)" :alt="'Drawdown Image: ' + pattern.Tooltip" />
                </router-link>
            </div>

            <hr/>
            <h2>Thumbnails Size</h2>

            <div>Choose the size of thumbnail images to show while displaying draft search results.</div>
            <br/>
            <b-form-select v-model='thumbnailSize' :options='thumbnailSizeOptions' @change='setThumbnailsSize' style="width:auto;" class="form-control inline-auto-width" />

            <hr/>
            <h2>Filtering</h2>
            <div>Choose to filter search drafts for shafts, treadles, and float lengths.  You can use this to only see drafts that can be woven on your looms or for other purposes.</div>
            <br/>
            <b-row>
                <b-col sm='5' class="small-top-padding">
                    <div class='inline-auto-width small-horizontal-padding'>
                        Min&nbsp;Shafts:<br />
                        <b-form-select v-model="minShafts" :options='minShaftsOptions' @change='setMinShafts' />
                    </div>
                    <div class='inline-auto-width small-horizontal-padding'>
                    Max&nbsp;Shafts:<br />
                    <b-form-select v-model="maxShafts" :options='maxShaftsOptions' @change='setMaxShafts' />
                    </div>
                    <div class='inline-auto-width small-horizontal-padding'>                
                        Max&nbsp;Treadles:<br />
                        <b-form-select v-model="maxTreadles" :options='maxTreadlesOptions' @change='setMaxTreadles' />
                    </div>
                    <div class='inline-auto-width small-horizontal-padding'>                
                        Max&nbsp;Float:<br />
                        <b-form-select v-model="maxFloatLength" :options='maxFloatLengthOptions' @change='setMaxFloatLength' />
                    </div>
                    <a v-if='(this.minShafts > 0 || this.maxShafts > 0 || this.maxTreadles > 0 || this.maxFloatLength > 0)' href='#' @click='clearAllFilters'>Clear&nbsp;Filters</a>
                </b-col>
            </b-row>
            <br/>
        </div>
        <help-us-button/>
        <mail-chimp-signup/>
    </b-container>

</template>

<script>
import dataService from '../mixins/dataService.js'
import mediaService from '../mixins/mediaService.js'
import urlsService from '../mixins/urlsService.js'
import helpUsButton from './HelpUsButton.vue'
import mailChimpSignup from './MailChimpSignup.vue'
import utilityService from '../mixins/utilityService.js'
import loginControl from './LoginControl.vue'

export default {
    mixins: [dataService, mediaService, urlsService, utilityService],
    data: function() {
        return {
            orientation: 'tl',
            thumbnailSize: 1,
            thumbnailSizeOptions: [
                { text: 'Small Thumbnails', value: 1 },
                { text: 'Medium Thumbnails', value: 2 },
                { text: 'Large Thumbnails', value: 3 },
                { text: 'Largest Thumbnails', value: 4 }
            ],
            minShafts: 0,
            maxShafts: 0,
            maxTreadles: 0,
            maxFloatLength: 0,
            patternId: 45026,
            pattern: null
        }
    },

    methods: {
        setMinShafts(s) {
            this.minShafts = s // NOTE: This is needed due to a timing problem, where the onchange event is triggered prior to the bound variable actually changing, the new value is passed as the sole parameter, s in this case
            this.saveAllUserSettings()
        },

        setMaxShafts(s) {
            this.maxShafts = s // NOTE: This is needed due to a timing problem, where the onchange event is triggered prior to the bound variable actually changing, the new value is passed as the sole parameter, s in this case
            this.saveAllUserSettings()
        },

        setMaxTreadles(t) {
            this.maxTreadles = t // NOTE: This is needed due to a timing problem, where the onchange event is triggered prior to the bound variable actually changing, the new value is passed as the sole parameter, t in this case
            this.saveAllUserSettings()
        },

        setMaxFloatLength(f) {
            this.maxFloatLength = f // NOTE: This is needed due to a timing problem, where the onchange event is triggered prior to the bound variable actually changing, the new value is passed as the sole parameter, t in this case
            this.saveAllUserSettings()
        },

        clearAllFilters() {
            this.minShafts = 0
            this.maxShafts = 0
            this.maxTreadles = 0
            this.maxFloatLength = 0
            this.saveAllUserSettings()
        },

        saveAllUserSettings() {
            let userSettings = this.$store.state.authUserSettings
            userSettings.MinShafts = this.minShafts
            userSettings.MaxShafts = this.maxShafts
            userSettings.MaxTreadles = this.maxTreadles
            userSettings.MaxFloatLength = this.maxFloatLength
            userSettings.Orientation = this.orientation
            userSettings.ThumbnailSize = this.thumbnailSize
            this.$store.commit('setFilters', userSettings)
            // console.log('All settings: ' + JSON.stringify(userSettings))
            this.putUserSettings(this.authUser.UserID, userSettings, this.authUser.LoginToken)
        },

        setThumbnailsSize(newThumbnailSize) {
            this.thumbnailSize = parseInt(newThumbnailSize) // needed because change events pass the value that the item is about to be changed to as a parameter, changing it later on the next tick instead
            this.saveAllUserSettings()
        },

        generateNumericOptionsList(defaultPrompt, defaultValue, numericValuesArray) {
            let options = []
            options.push({ text: defaultPrompt, value: defaultValue })
            for (let c=0; c<numericValuesArray.length; c++) {
                let val = numericValuesArray[c]
                options.push({ text: val + '', value: val })
            }
            return options
        }
    },

    components: {
        helpUsButton: helpUsButton,
        mailChimpSignup: mailChimpSignup,
        loginControl: loginControl
    },

    watch: {
        'orientation': function() {
            this.saveAllUserSettings()
        }
    },

    computed: {
        authUser: function() {
            return this.$store.state.authUser
        },
        isAuthenticated: function() {
            return this.$store.getters.isAuthenticated
        },
        authUserSettings: function() {
            return this.$store.state.authUserSettings
        },
        minShaftsOptions: function() {
            return this.generateNumericOptionsList('(Any)', 0, [2, 3, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 32, 40, 48])
        },
        maxShaftsOptions: function() {
            return this.generateNumericOptionsList('(Any)', 0, [2, 3, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 32, 40, 48])
        },
        maxTreadlesOptions: function() {
            return this.generateNumericOptionsList('(Any)', 0, [2, 3, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24])
        },
        maxFloatLengthOptions: function() {
            return this.generateNumericOptionsList('(Any)', 0, [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20])
        },
        draftImageUrlFunction: function() {
            return this.$store.state.draftImageUrlFunction
        }
    },

    created() {
        this.$store.commit('setPageTitle', 'Your Site Settings')
        this.minShafts = this.authUserSettings.MinShafts
        this.maxShafts = this.authUserSettings.MaxShafts
        this.maxTreadles = this.authUserSettings.MaxTreadles
        this.maxFloatLength = this.authUserSettings.MaxFloatLength
        this.thumbnailSize = this.authUserSettings.ThumbnailSize
        this.orientation = this.authUserSettings.Orientation

        this.getPattern(this.patternId).then((resp) => {
            // console.log('in getPattern: ' + JSON.stringify(resp))
            this.pattern = resp.data
        })
    }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/Settings.vue